/* Bileşen blokları — bugünkü sayfanın etkileşimli parçaları.

   İşaretlemeleri templates/blocks/<tip>.html'de, bugünkü bölüm
   şablonlarından birebir kopyalandı: main.js `data-coverflow`, `data-reel`,
   `data-digital` gibi kancalara bağlı ve onları değiştirmek davranışı
   bozardı. Burada yalnızca şablonun kapsamı kuruluyor — her metin alanı
   `field()`'dan geçiyor (temizlenmiş HTML, i18n anahtarı, düzenleme işareti),
   her kart ve satır düzenleyici için `data-i` taşıyor. */

import { escapeText, type Block, type HeroLine, type Rich } from '@frawgraphy/shared';
import { render } from '../template.ts';
import { button, escapeAttr, field, mediaSrc, renderBlocks, type RenderCtx } from './render.ts';

type Of<T extends Block['type']> = Extract<Block, { type: T }>;

/** Bileşenin kökündeki işaretler: düzenleme kimliği ve stil kancası. */
function root(block: Block, ctx: RenderCtx): string {
  const parts: string[] = [];
  if (block.style && (block.style.color || block.style.bg || block.style.scale || block.style.font || block.style.padY || block.style.marginY)) {
    parts.push(`data-bid="${escapeAttr(block.id)}"`);
  }
  if (ctx.edit) parts.push(`data-b="${escapeAttr(block.id)}"`);
  return parts.length ? ` ${parts.join(' ')}` : '';
}

const itemEdit = (id: string, ctx: RenderCtx) => (ctx.edit ? ` data-i="${escapeAttr(id)}"` : '');

/** Kart/öge alanının anahtarı: öğenin eski anahtarı varsa onunla, yoksa kimliğiyle. */
const itemKey = (item: { id: string; key?: string | undefined }, name: string) => `${item.key ?? `b.${item.id}`}.${name}`;

function imgTag(media: Parameters<typeof mediaSrc>[0], ctx: RenderCtx): string {
  const m = mediaSrc(media, ctx);
  if (!m) return '';
  const size = m.width && m.height ? ` width="${m.width}" height="${m.height}"` : '';
  return `<img src="${escapeAttr(m.src)}" alt="${m.alt}"${size} loading="lazy" decoding="async">`;
}

/** Hero satırının büyük harfli hâli; varlık (`&amp;`) dokunulmadan kalır. */
function upper(escaped: string): string {
  return escaped.replace(/&[a-z0-9#]+;|[^&]+/gi, (part) => (part.startsWith('&') ? part : part.toUpperCase()));
}

/** Satır başına derinlik — bugünkü hero'nun paralaks katsayıları. */
const DEPTHS = ['-0.02', '-0.035', '-0.05', '-0.065', '-0.08'];

export function renderComponent(block: Block, ctx: RenderCtx): string {
  const tpl = ctx.templates?.[templateName(block)];
  if (tpl === undefined) {
    return ctx.edit ? `<div data-b="${escapeAttr(block.id)}" class="b-missing">${escapeText(block.type)}</div>` : '';
  }
  return render(tpl, { ...(ctx.pageScope ?? {}), ...scopeOf(block, ctx) }, ctx.partials ?? {});
}

function templateName(block: Block): string {
  if (block.type === 'cards' || block.type === 'facts') return `${block.type}-${block.variant}`;
  if (block.type === 'nav') return 'site-header';
  if (block.type === 'footer') return 'site-footer';
  return block.type;
}

/** Menü bloğunun telefon menüsü hâli — aynı bağlantılar, ayrı işaretleme. */
export function renderMobileMenu(block: Of<'nav'>, ctx: RenderCtx): string {
  const tpl = ctx.templates?.['site-menu'];
  if (tpl === undefined) return '';
  return render(tpl, { ...(ctx.pageScope ?? {}), ...nav(block, ctx) }, ctx.partials ?? {});
}

/**
 * Bağlantıyı sayfanın derinliğine göre çözer: `#work` ana sayfada `#work`,
 * iç sayfada `index.html#work`, blog yazısında `../index.html#work`; göreli
 * sayfa (`blog.html`) sayfanın önekini alır. Mutlak adresler olduğu gibi.
 */
function resolveHref(href: string, ctx: RenderCtx): string {
  const scope = ctx.pageScope ?? {};
  const home = typeof scope['home'] === 'string' ? scope['home'] : '';
  const base = typeof scope['base'] === 'string' ? scope['base'] : '';
  if (href.startsWith('#')) return `${home}${href}`;
  if (/^[a-z0-9]/i.test(href) && !href.includes(':')) return `${base}${href}`;
  return href;
}

/**
 * Anahtarı olmayan ve İngilizcesi girilmemiş bir metin bugünkü sitede
 * çevrilmiyordu ("FRAWGRAPHY", "Instagram"): i18n özniteliği basılmıyor.
 * Biri EN girdiği an anahtar kazanıyor.
 */
function optional(rich: Rich, key: string | undefined, fallbackKey: string, path: string, ctx: RenderCtx, htmlMode: boolean) {
  if (key || rich.en) return field(rich, key ?? fallbackKey, path, ctx, htmlMode);
  return { attrs: ctx.edit ? ` data-f="${escapeAttr(path)}"` : '', html: rich.tr };
}

function nav(b: Of<'nav'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    brandSub: field(b.brandSub, b.key ? `${b.key}.sub` : 'brand.sub', 'brandSub', ctx, false),
    location: field(b.location, 'menu.location', 'location', ctx, false),
    items: b.items
      .map((link, i) => ({ link, i }))
      .filter(({ link }) => !(link.href.startsWith('#') && ctx.anchors && !ctx.anchors.has(link.href.slice(1))))
      .map(({ link, i }) => ({
        edit: itemEdit(link.id, ctx),
        href: resolveHref(link.href, ctx),
        label: field(link.label, link.key ?? `b.${link.id}.label`, `items.${i}.label`, ctx, false)
      }))
  };
}

function footer(b: Of<'footer'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    mark: optional(b.mark, undefined, `b.${b.id}.mark`, 'mark', ctx, false),
    legal: field(b.legal, 'footer.legal', 'legal', ctx, true),
    links: b.links.map((link, i) => ({
      edit: itemEdit(link.id, ctx),
      href: resolveHref(link.href, ctx),
      lang: link.lang,
      label: optional(link.label, link.key, `b.${link.id}.label`, `links.${i}.label`, ctx, false)
    }))
  };
}

function pagecta(b: Of<'pagecta'>, ctx: RenderCtx) {
  const k = b.key ?? `b.${b.id}`;
  return {
    root: root(b, ctx),
    title: field(b.title, `${k}.ctaTitle`, 'title', ctx, false),
    text: field(b.text, `${k}.ctaText`, 'text', ctx, false),
    buttons: b.buttons.map((x, i) => button(x, ctx, `buttons.${i}`)).join('\n')
  };
}

function scopeOf(block: Block, ctx: RenderCtx): Record<string, unknown> {
  switch (block.type) {
    case 'hero':
      return hero(block, ctx);
    case 'coverflow':
      return coverflow(block, ctx);
    case 'reels':
      return reels(block, ctx);
    case 'cards':
      return block.variant === 'ai' ? aiCards(block, ctx) : serviceCards(block, ctx);
    case 'facts':
      return stats(block, ctx);
    case 'split':
      return split(block, ctx);
    case 'cta':
      return cta(block, ctx);
    case 'contact':
      return contact(block, ctx);
    case 'nav':
      return nav(block, ctx);
    case 'footer':
      return footer(block, ctx);
    case 'pagecta':
      return pagecta(block, ctx);
    default:
      return { root: root(block, ctx) };
  }
}

function hero(b: Of<'hero'>, ctx: RenderCtx) {
  const plate = b.media ? mediaSrc(b.media, ctx)?.src ?? null : null;
  return {
    root: root(b, ctx),
    plate,
    eyebrow: field(b.eyebrow, b.key ? `${b.key}.eyebrow` : `b.${b.id}.eyebrow`, 'eyebrow', ctx, false),
    linesEdit: ctx.edit ? ' data-f="lines"' : '',
    lines: b.lines.map((l: HeroLine, i) => ({ text: l.text, upper: upper(l.text), indent: l.indent, amp: l.amp, depth: DEPTHS[i] ?? DEPTHS[DEPTHS.length - 1] })),
    lede: field(b.lede, b.key ? `${b.key}.lede` : `b.${b.id}.lede`, 'lede', ctx, false),
    buttons: b.buttons.map((x, i) => button(x, ctx, `buttons.${i}`)).join('\n'),
    hint: b.hint ? field(b.hint, b.key ? `${b.key}.hint` : `b.${b.id}.hint`, 'hint', ctx, false) : null
  };
}

function coverflow(b: Of<'coverflow'>, ctx: RenderCtx) {
  const featured = b.photos
    .map((id) => {
      const p = ctx.photo(id);
      if (!p?.info) return null;
      return { id, browse: p.browse, full: p.full, alt: p.info.alt, width: p.info.width, height: p.info.height };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  return { root: root(b, ctx), featured, more: b.more ? button(b.more, ctx, 'more') : null };
}

function reels(b: Of<'reels'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    items: b.items.map((r, i) => {
      const [fx, fy] = (r.focus ?? '50% 50%').split(' ');
      const titleKey = itemKey(r, 'title');
      return {
        edit: itemEdit(r.id, ctx),
        /* Aynı başlık iki yerde: düğmenin gizli etiketi ve görünen başlık.
           Düzenleme yalnızca görünende; gizli olan aynı anahtarı paylaşıyor. */
        hidden: field(r.title, titleKey, `items.${i}.title-hidden`, { ...ctx, edit: false }, false),
        title: field(r.title, titleKey, `items.${i}.title`, ctx, false),
        spec: field(r.spec, itemKey(r, 'spec'), `items.${i}.spec`, ctx, false),
        note: field(r.note, itemKey(r, 'note'), `items.${i}.note`, ctx, false),
        poster: r.poster ?? '',
        video: r.video ?? '',
        time: r.time,
        label: r.label?.tr ?? '',
        fx,
        fy
      };
    })
  };
}

function aiCards(b: Of<'cards'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    items: b.items.map((c, i) => ({
      edit: itemEdit(c.id, ctx),
      full: c.full ?? (c.media ? mediaSrc(c.media, ctx)?.full ?? '' : ''),
      img: c.media ? imgTag(c.media, ctx) : '',
      /* Bugünkü sitede kategori anahtarı bütün kartlarda ortak (`digital.cat`). */
      cat: field(c.category ?? { tr: '' }, b.key && c.key ? `${b.key}.cat` : itemKey(c, 'cat'), `items.${i}.category`, ctx, false),
      year: c.year ?? '',
      title: field(c.title, itemKey(c, 'title'), `items.${i}.title`, ctx, false),
      desc: field(c.desc ?? { tr: '' }, itemKey(c, 'desc'), `items.${i}.desc`, ctx, false)
    }))
  };
}

function serviceCards(b: Of<'cards'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    items: b.items.map((c, i) => ({
      edit: itemEdit(c.id, ctx),
      englishTitle: c.titleLang === 'en',
      title: field(c.title, itemKey(c, 'title'), `items.${i}.title`, ctx, false),
      desc: field(c.desc ?? { tr: '' }, itemKey(c, 'desc'), `items.${i}.desc`, ctx, false),
      meta: field(c.meta ?? { tr: '' }, itemKey(c, 'meta'), `items.${i}.meta`, ctx, false)
    }))
  };
}

function stats(b: Of<'facts'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    items: b.items.map((f, i) => ({
      edit: itemEdit(f.id, ctx),
      label: field(f.label, f.key ?? itemKey(f, 'label'), `items.${i}.label`, ctx, false),
      /* Sayılar çevrilmiyor: bugünkü sitede `dd`'de anahtar yok. */
      value: { attrs: ctx.edit ? ` data-f="items.${i}.value"` : '', html: f.value.tr }
    }))
  };
}

function split(b: Of<'split'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    img: imgTag(b.media, ctx),
    caption: b.caption ? field(b.caption, b.captionKey ?? `b.${b.id}.caption`, 'caption', ctx, false) : null,
    body: renderBlocks(b.blocks, ctx)
  };
}

function cta(b: Of<'cta'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    text: field(b.text, b.key ?? `b.${b.id}.text`, 'text', ctx, false),
    button: button(b.button, ctx, 'button')
  };
}

function contact(b: Of<'contact'>, ctx: RenderCtx) {
  return {
    root: root(b, ctx),
    info: b.info.map((f, i) => ({
      label: field(f.label, f.key ?? itemKey(f, 'label'), `info.${i}.label`, ctx, false),
      value: f.valueKey
        ? field(f.value, f.valueKey, `info.${i}.value`, ctx, true)
        : { attrs: ctx.edit ? ` data-f="info.${i}.value"` : '', html: f.value.tr }
    })),
    note: b.note ? field(b.note, b.key ? `${b.key}.note` : `b.${b.id}.note`, 'note', ctx, false) : null
  };
}
